import { useSuccessFeeTermsQuery } from '@/hooks/queries/use-success-fee-terms';
import type React from 'react';
import { ShopifyApprovalNotice } from './ShopifyApprovalNotice';
import { TermsAcceptanceNotice } from './TermsAcceptanceNotice';

/**
 * The notice, for every brand user, while anything is still owed on the rollout.
 *
 * It carries two moments rather than one, because the rollout has two steps and
 * the deadline belongs to both. Accepting the terms settles the first; the lock
 * still closes the dashboard of an organization that never approved the
 * replacement in Shopify.
 *
 * **Which organizations owe that approval is not recomputed here.** The backend
 * already decides it, in one place, and says so by serving a deadline on a
 * state that is otherwise silent: `not_required` carries one in exactly the
 * branch where the terms are accepted, the organization is billable, the notice
 * is open, the deadline has not passed and the migration is not settled. Every
 * other way of reaching `not_required` serves none. Reading that signal rather
 * than rebuilding the rule out of `acceptedVersion`, the account type and the
 * migration state is what keeps this notice and the lock agreeing on who is
 * about to lose their dashboard: a second predicate would eventually disagree
 * with the first, and the shape of that disagreement is a merchant locked out
 * with nothing on screen.
 */
export const SuccessFeeTermsBanner: React.FC = () => {
  const { data } = useSuccessFeeTermsQuery();
  const state = data?.state;

  if (data && state?.state === 'pending_notice') {
    return <TermsAcceptanceNotice state={data.state} document={data.document} />;
  }

  if (state?.state === 'not_required' && state.deadline !== null) {
    return <ShopifyApprovalNotice deadline={state.deadline} />;
  }

  return null;
};
